import { z } from 'zod';
import { longText, name, uuid } from './fields';

/**
 * Master Lists — reusable template lists (data-model.md §3, api-contract §2).
 * Owner-scoped in Phase 1. The response intentionally omits items and item count
 * (api-contract §2 note) — items are fetched via the nested items routes (§3).
 */

/** Full Master List resource (response shape — api-contract §2). */
export const MasterList = z.object({
  id: uuid(),
  ownerId: uuid(),
  name: name(),
  description: longText(),
  lastEditedById: uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type MasterList = z.infer<typeof MasterList>;

/** A Master List plus its current item count — used by the library grid cards. */
export const MasterListWithCount = MasterList.extend({
  itemCount: z.number().int().nonnegative(),
});
export type MasterListWithCount = z.infer<typeof MasterListWithCount>;

/**
 * POST /api/master-lists body (api-contract §2.2). Server sets `ownerId`,
 * `lastEditedById`, `id`, timestamps. `description` optional/nullable.
 */
export const CreateMasterListInput = z.object({
  name: name(),
  description: longText().optional(),
});
export type CreateMasterListInput = z.infer<typeof CreateMasterListInput>;

/** PATCH /api/master-lists/:listId body (api-contract §2.4) — all optional. */
export const UpdateMasterListInput = z
  .object({
    name: name(),
    description: longText(),
  })
  .partial();
export type UpdateMasterListInput = z.infer<typeof UpdateMasterListInput>;

/**
 * POST /api/master-lists/:listId/duplicate body (api-contract §2.6). Optional
 * name override; default is `"<source name> (copy)"` (resolved server-side).
 */
export const DuplicateMasterListInput = z
  .object({
    name: name(),
  })
  .partial();
export type DuplicateMasterListInput = z.infer<typeof DuplicateMasterListInput>;
